import type { DocJSON, PublishedGlossary } from "./types";

// Hand-authored fixture mimicking what the backend processor would emit
// for a small API-docs Markdown + OpenAPI input. Used by the demo screen
// and by the slice-1 tests. Once the backend ships, this is replaced by
// a real `/docs/:id/render` fetch.

export const FIXTURE_DOC: DocJSON = {
  doc_id: "demo-webhooks",
  pipeline_version: 1,
  format: "markdown",
  title: "Webhooks — a guided tour",
  citations: [],
  sections: [
    {
      id: "introduction",
      level: 1,
      heading: "Introduction",
      blocks: [
        {
          id: "introduction-b0",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "A webhook is an HTTP request your application sends to a URL " +
                "the customer configured, the moment something interesting happens " +
                "in their account. Every webhook carries an Idempotency-Key in its " +
                "headers so receivers can safely retry without double-processing.",
            },
          ],
        },
        {
          id: "introduction-b1",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "Receivers verify each webhook's authenticity using the Webhook-Signature header, " +
                "which is an HMAC-SHA256 hash of the body concatenated with the request timestamp.",
            },
          ],
        },
      ],
    },
    {
      id: "delivery",
      level: 1,
      heading: "Delivery semantics",
      blocks: [
        {
          id: "delivery-b0",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "We attempt at-least-once delivery with exponential backoff. If a receiver returns " +
                "a 2xx, the webhook is considered delivered. Any other response — including a 5xx " +
                "or a network timeout — triggers a retry policy that doubles the delay between " +
                "attempts, capped at 8 hours.",
            },
          ],
        },
        {
          id: "delivery-b1",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "Each delivery attempt produces a DeliveryAttempt record visible in the dashboard. " +
                "After 72 hours of repeated failure the webhook is marked failed and is moved to the " +
                "dead-letter queue, where it waits for manual intervention.",
            },
          ],
        },
      ],
    },
    {
      id: "errors",
      level: 1,
      heading: "Error responses",
      blocks: [
        {
          id: "errors-b0",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "If the receiver returns 410 Gone, the endpoint is disabled and no further " +
                "deliveries are attempted. A 429 Too Many Requests response shortens the retry " +
                "interval to respect the receiver's rate-limit signaling.",
            },
          ],
        },
        {
          id: "errors-b1",
          kind: "paragraph",
          inline: [
            {
              kind: "text",
              text:
                "Signature verification failures on the receiver side are not surfaced as errors " +
                "to us — they appear as a successful 2xx from our perspective. Operators should " +
                "audit their own logs for HMAC mismatches.",
            },
          ],
        },
      ],
    },
  ],
};

export const FIXTURE_GLOSSARY: PublishedGlossary = {
  doc_id: "demo-webhooks",
  extraction_glossary_version: 1,
  published_version: 1,
  published_at: "2026-05-20T17:00:00Z",
  published_by: "demo",
  terms: [
    {
      term_id: "webhook",
      term: "webhook",
      alternates: [],
      definition:
        "An HTTP request your application sends to a customer-configured URL when an event occurs.",
      kind: "concept",
      origin: "openapi",
    },
    {
      term_id: "idempotency-key",
      term: "Idempotency-Key",
      alternates: [],
      definition:
        "Header attached to each webhook so receivers can safely retry without double-processing.",
      kind: "param",
      origin: "openapi",
    },
    {
      term_id: "webhook-signature",
      term: "Webhook-Signature",
      alternates: [],
      definition:
        "Header containing the HMAC-SHA256 hash of the body + timestamp. Receivers verify it to confirm authenticity.",
      kind: "param",
      origin: "openapi",
    },
    {
      term_id: "hmac-sha256",
      term: "HMAC-SHA256",
      alternates: ["HMAC"],
      definition: "Keyed-hash message authentication code based on SHA-256.",
      kind: "concept",
      origin: "llm",
      source_block_id: "introduction-b1",
    },
    {
      term_id: "at-least-once",
      term: "at-least-once delivery",
      alternates: [],
      definition:
        "Delivery guarantee that every event reaches the receiver at least once; receivers must be prepared for duplicates.",
      kind: "concept",
      origin: "llm",
      source_block_id: "delivery-b0",
    },
    {
      term_id: "exponential-backoff",
      term: "exponential backoff",
      alternates: [],
      definition: "Retry strategy where the delay between attempts doubles each time, with an upper cap.",
      kind: "concept",
      origin: "llm",
      source_block_id: "delivery-b0",
    },
    {
      term_id: "retry-policy",
      term: "retry policy",
      alternates: [],
      definition:
        "Rules governing when, how often, and for how long a webhook delivery is retried after failure.",
      kind: "concept",
      origin: "llm",
      source_block_id: "delivery-b0",
    },
    {
      term_id: "delivery-attempt",
      term: "DeliveryAttempt",
      alternates: [],
      definition: "A single record of one webhook send, visible in the dashboard.",
      kind: "type",
      origin: "openapi",
      source_block_id: "delivery-b1",
    },
    {
      term_id: "dead-letter-queue",
      term: "dead-letter queue",
      alternates: ["DLQ"],
      definition: "Where webhooks that have failed past the retry window are parked for manual review.",
      kind: "concept",
      origin: "llm",
      source_block_id: "delivery-b1",
    },
    {
      term_id: "410-gone",
      term: "410 Gone",
      alternates: [],
      definition: "Receiver response that disables the endpoint permanently — no further attempts.",
      kind: "error",
      origin: "openapi",
      source_block_id: "errors-b0",
    },
    {
      term_id: "429-too-many-requests",
      term: "429 Too Many Requests",
      alternates: [],
      definition: "Receiver response signaling rate-limit pressure; retries lengthen the interval.",
      kind: "error",
      origin: "openapi",
      source_block_id: "errors-b0",
    },
    {
      term_id: "signature-verification",
      term: "signature verification",
      alternates: [],
      definition:
        "The receiver-side step of validating Webhook-Signature against the body + timestamp.",
      kind: "concept",
      origin: "llm",
      source_block_id: "errors-b1",
    },
  ],
};
